// ABOUTME: Main dashboard page displaying forecasts, schedules, and stock management.
// ABOUTME: Uses shopId prop to fetch shop-specific data from the multi-shop API.

import React, { useState, useEffect, useRef, useCallback, Suspense, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, CircularProgress, Alert, Collapse } from '@mui/material';
import axios from 'axios';
import TestModeControl from '../components/TestModeControl';
import StockAdjustment from '../components/StockAdjustment';
import ScheduleHeader from '../components/ScheduleHeader';
import LimitedHistoryBanner from '../components/LimitedHistoryBanner';
import { useDashboard } from '../contexts/DashboardContext';
import { useShop } from '../contexts/ShopContext';
import {
  UrgentTasksSection,
  PlannedTasksSection,
  IgnoredTasksSection,
  FailedProductsSection,
  PendingProductsSection,
  ProducedElsewhereSection,
} from '../components/schedule';
import API_BASE_URL from '../config';

const ForecastChart = lazy(() => import('../components/ForecastChart'));

function Dashboard({ shopId }) {
  const { t } = useTranslation();
  const { setDashboardState } = useDashboard();
  const { shops, currentShopId, switchShop, loading: shopsLoading } = useShop();
  const [dashboardData, setDashboardData] = useState(null);
  const [products, setProducts] = useState([]);
  const [schedule, setSchedule] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(() => {
    const saved = localStorage.getItem('selectedProduct');
    return saved ? parseInt(saved) : null;
  });
  const [currentTime, setCurrentTime] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [testModeEnabled, setTestModeEnabled] = useState(false);
  const [chartRefreshTrigger, setChartRefreshTrigger] = useState(0);
  const [isNextDayMode, setIsNextDayMode] = useState(false);
  const [failedExpanded, setFailedExpanded] = useState(false);
  const [ignoredExpanded, setIgnoredExpanded] = useState(false);
  const [producedElsewhereExpanded, setProducedElsewhereExpanded] = useState(false);
  const [groupByCategory, setGroupByCategory] = useState(false);
  const [chartExpanded, setChartExpanded] = useState(false);
  const [trendWeight, setTrendWeight] = useState(1.0);
  const [savedTrendWeight, setSavedTrendWeight] = useState(1.0);
  const [trendWeightLoading, setTrendWeightLoading] = useState(false);
  const hasInitializedProduct = useRef(false);
  const productsLoaded = useRef(false);
  const chartRef = useRef(null);

  // Virtual shops aggregate member shops: no per-product chart and no trend slider
  const currentShop = shops?.find((shop) => shop.shop_id === shopId);
  const isVirtualShop = !!currentShop?.virtual;

  // Check URL parameter for test mode visibility
  const showTestMode = React.useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return process.env.REACT_APP_SHOW_TEST_MODE === 'true' || urlParams.get('testMode') === 'true';
  }, []);

  // Fetch trend adjustment when shop changes
  useEffect(() => {
    if (!shopId) return;
    const fetchTrendAdjustment = async () => {
      setTrendWeightLoading(true);
      try {
        const response = await axios.get(`${API_BASE_URL}/shops/${shopId}/forecasts/trend-adjustment`);
        const value = response.data.value ?? 1.0;
        setTrendWeight(value);
        setSavedTrendWeight(value);
      } catch (err) {
        setTrendWeight(1.0);
        setSavedTrendWeight(1.0);
      } finally {
        setTrendWeightLoading(false);
      }
    };
    fetchTrendAdjustment();
  }, [shopId]);

  // Debounced auto-save when trend slider changes
  useEffect(() => {
    if (!shopId) return;
    if (trendWeight === savedTrendWeight) return;

    const timer = setTimeout(async () => {
      try {
        await axios.put(`${API_BASE_URL}/shops/${shopId}/forecasts/trend-adjustment`, {
          value: trendWeight
        });
        setSavedTrendWeight(trendWeight);
      } catch (err) {
        console.error('Error auto-saving trend adjustment:', err);
      }
      setChartRefreshTrigger((prev) => prev + 1);
    }, 300);

    return () => clearTimeout(timer);
  }, [shopId, trendWeight, savedTrendWeight]);

  const handleTrendWeightChange = (newValue) => {
    setTrendWeight(newValue);
    setChartExpanded(true);
  };

  const handleResetTrendWeight = async () => {
    try {
      await axios.delete(`${API_BASE_URL}/shops/${shopId}/forecasts/trend-adjustment`);
      setTrendWeight(1.0);
      setSavedTrendWeight(1.0);
      setChartExpanded(true);
    } catch (err) {
      console.error('Error resetting trend adjustment:', err);
    }
  };

  const handleReloadConfig = useCallback(async () => {
    try {
      await axios.post(`${API_BASE_URL}/config/reload`);
      window.location.reload();
    } catch (err) {
      console.error('Failed to reload config:', err);
      setError(t('dashboard.reloadConfigError'));
    }
  }, [t]);

  // Update dashboard state in context for Layout to use
  useEffect(() => {
    setDashboardState({
      currentTime,
      dashboardData,
      onReloadConfig: handleReloadConfig,
    });
  }, [currentTime, dashboardData, handleReloadConfig, setDashboardState]);

  // Fetch products once on mount (rarely changes)
  useEffect(() => {
    if (!shopId) return;
    if (productsLoaded.current) return;

    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/shops/${shopId}/baker/products`);
        const productsList = response.data;
        setProducts(productsList);
        productsLoaded.current = true;

        // Initialize selected product (only from active products)
        const activeProducts = productsList.filter((p) => p.is_active === true);
        if (activeProducts.length > 0 && !hasInitializedProduct.current) {
          hasInitializedProduct.current = true;
          if (!selectedProduct) {
            setSelectedProduct(activeProducts[0].product_id);
          } else {
            const productStillActive = activeProducts.some((p) => p.product_id === selectedProduct);
            if (!productStillActive) {
              setSelectedProduct(activeProducts[0].product_id);
            }
          }
        }
      } catch (err) {
        console.error('Failed to fetch products:', err);
      }
    };

    fetchProducts();
  }, [shopId, selectedProduct]);

  // Fetch dashboard data on mount
  useEffect(() => {
    if (!shopId) return;
    fetchDashboardData(true);
  }, [shopId]);

  // Poll for updates - faster (10s) when waiting for live data, slower (60s) when live
  // Skip polling when in next day mode (data is static)
  useEffect(() => {
    if (!shopId) return;

    // Always update the clock
    const timeInterval = setInterval(() => setCurrentTime(new Date()), 1000);

    // Skip data refresh polling when in next day mode
    if (isNextDayMode) {
      return () => clearInterval(timeInterval);
    }

    const interval = dashboardData?.is_live_data === false ? 10000 : 60000;
    const refreshInterval = setInterval(() => {
      fetchDashboardData(false);
    }, interval);

    return () => {
      clearInterval(refreshInterval);
      clearInterval(timeInterval);
    };
  }, [shopId, dashboardData?.is_live_data, isNextDayMode]);

  // Refetch when next day mode changes
  useEffect(() => {
    if (!shopId || loading) return;
    fetchDashboardData(false);
  }, [isNextDayMode]);

  // Refetch when shop transitions to after closing (to get next opening data)
  useEffect(() => {
    if (!shopId || loading) return;
    if (schedule?.is_after_closing && !schedule?.next_opening_info) {
      // Shop just closed but we don't have next opening info yet - refetch with next_day=true
      fetchDashboardData(false);
    }
  }, [schedule?.is_after_closing]);

  const fetchDashboardData = async (isInitialLoad = false) => {
    if (!shopId) return;
    try {
      if (isInitialLoad) {
        setLoading(true);
      }

      // Use next opening mode when manually enabled OR when shop is after closing
      // We check schedule?.is_after_closing from previous fetch for auto-enabling
      const shouldUseNextOpening = isNextDayMode || schedule?.is_after_closing;

      const [dashboard, inst] = await Promise.all([
        axios.get(`${API_BASE_URL}/shops/${shopId}/owner/dashboard`),
        axios.get(`${API_BASE_URL}/shops/${shopId}/baker/instructions`, {
          params: { hours_ahead: 2, next_day: shouldUseNextOpening },
        }),
      ]);

      setDashboardData(dashboard.data);
      setSchedule(inst.data);
      setError(null);

      if (!isInitialLoad) {
        setChartRefreshTrigger((prev) => prev + 1);
      }
    } catch (err) {
      setError('Failed to fetch dashboard data');
      console.error(err);
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      }
    }
  };

  const handleTestModeChange = (enabled) => {
    setTestModeEnabled(enabled);
  };

  const handleStockChange = () => {
    setChartRefreshTrigger((prev) => prev + 1);
  };

  const handleProductSelect = useCallback((productId) => {
    setSelectedProduct(productId);
    localStorage.setItem('selectedProduct', productId.toString());
    setChartExpanded(true);
    // Scroll to chart after state updates
    setTimeout(() => {
      chartRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }, []);

  const handleTimeChange = (newTime) => {
    setCurrentTime(newTime);
    setTimeout(() => {
      fetchDashboardData();
    }, 200);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        {t('common.error')}: {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Warning while the shop has too little sales history for weekday patterns */}
      <LimitedHistoryBanner patternStatus={schedule?.pattern_status} />

      {/* Test Mode Control */}
      {showTestMode && (
        <Box sx={{ mb: 3 }}>
          <TestModeControl onTestModeChange={handleTestModeChange} onTimeChange={handleTimeChange} />
        </Box>
      )}

      {/* Collapsible Forecast Chart at Top - lazy loaded */}
      <Collapse in={chartExpanded && !isVirtualShop}>
        <Box ref={chartRef} sx={{ mb: 3 }}>
          {!isVirtualShop && selectedProduct && (
            <Suspense fallback={<Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>}>
              <ForecastChart
                shopId={shopId}
                product={selectedProduct}
                refreshTrigger={chartRefreshTrigger}
                products={products}
                onProductChange={(newProductId) => {
                  setSelectedProduct(newProductId);
                  localStorage.setItem('selectedProduct', newProductId.toString());
                }}
                trendWeight={trendWeight}
                hideTrendSlider
              />
            </Suspense>
          )}
        </Box>
      </Collapse>

      {/* Production Schedule Header */}
      <ScheduleHeader
        schedule={schedule}
        isNextDayMode={isNextDayMode}
        onNextDayModeChange={setIsNextDayMode}
        groupByCategory={groupByCategory}
        onGroupByCategoryChange={setGroupByCategory}
        shops={shops}
        currentShopId={currentShopId}
        onShopChange={(e) => switchShop(e.target.value)}
        onDetailedShopSelector={() => {
          localStorage.removeItem('selected_shop');
          window.location.href = '/';
        }}
        shopsLoading={shopsLoading}
        onRefresh={() => window.location.reload()}
        showShopSelector={showTestMode}
        trendWeight={trendWeight}
        onTrendWeightChange={handleTrendWeightChange}
        onResetTrendWeight={handleResetTrendWeight}
        trendWeightLoading={trendWeightLoading}
        savedTrendWeight={savedTrendWeight}
        chartExpanded={chartExpanded}
        onChartExpandedChange={setChartExpanded}
        isVirtualShop={isVirtualShop}
      />

      {/* Urgent Tasks */}
      {schedule?.urgent_tasks?.length > 0 && (
        <UrgentTasksSection tasks={schedule.urgent_tasks} groupByCategory={groupByCategory} onProductSelect={handleProductSelect} testModeEnabled={testModeEnabled} />
      )}

      {/* Planned Tasks */}
      {schedule?.planned_tasks?.length > 0 && (
        <PlannedTasksSection tasks={schedule.planned_tasks} groupByCategory={groupByCategory} onProductSelect={handleProductSelect} testModeEnabled={testModeEnabled} />
      )}

      {/* Predictions being recomputed after a config change */}
      {schedule?.pending_products?.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <PendingProductsSection
            products={schedule.pending_products}
            onProductSelect={handleProductSelect}
          />
        </Box>
      )}

      {/* Products produced by another (virtual) shop */}
      {schedule?.produced_elsewhere_tasks?.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <ProducedElsewhereSection
            tasks={schedule.produced_elsewhere_tasks}
            expanded={producedElsewhereExpanded}
            onExpandChange={setProducedElsewhereExpanded}
            onProductSelect={handleProductSelect}
            testModeEnabled={testModeEnabled}
          />
        </Box>
      )}

      {/* Failed Products (forecast errors) */}
      {schedule?.failed_products?.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <FailedProductsSection
            products={schedule.failed_products}
            expanded={failedExpanded}
            onExpandChange={setFailedExpanded}
            onProductSelect={handleProductSelect}
          />
        </Box>
      )}

      {/* Ignored Tasks */}
      {schedule?.ignored_tasks?.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <IgnoredTasksSection
            tasks={schedule.ignored_tasks}
            expanded={ignoredExpanded}
            onExpandChange={setIgnoredExpanded}
            onProductSelect={handleProductSelect}
            testModeEnabled={testModeEnabled}
          />
        </Box>
      )}

      {/* Stock Management Section */}
      <Box sx={{ mt: 4 }}>
        <StockAdjustment
          shopId={shopId}
          testModeEnabled={testModeEnabled}
          currentTime={currentTime}
          onStockChange={handleStockChange}
          onProductSelect={handleProductSelect}
        />
      </Box>
    </Box>
  );
}

export default Dashboard;
